import { useState } from "react";
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FaArrowDown, FaArrowUp, FaGripVertical, FaSave, FaUndo } from "react-icons/fa";
import { PiSlidersBold } from "react-icons/pi";
import Title from "../../components/common/title/Title";
import { useSidebarSettings } from "../../layout/sidebar/SidebarSettingsContext";
import { DEFAULT_SIDEBAR_ORDER, SIDEBAR_ITEMS } from "../../layout/sidebar/sidebarItems";
import { successMessage } from "../../utils/toastify-message/ToastifyMessage";

const labels = new Map(SIDEBAR_ITEMS.map((item) => [item.id, item.label]));
const number = new Intl.NumberFormat("fa-IR");
const verticalOnly = ({ transform }) => ({ ...transform, x: 0 });
const announcements = {
  onDragStart: ({ active }) => `جابه‌جایی ${labels.get(active.id)} آغاز شد.`,
  onDragOver: ({ active, over }) => over ? `${labels.get(active.id)} در جای ${labels.get(over.id)} قرار می‌گیرد.` : "خارج از فهرست هستید.",
  onDragEnd: ({ active, over }) => over ? `جای ${labels.get(active.id)} تغییر کرد. برای اعمال، ذخیره را بزنید.` : "جابه‌جایی لغو شد.",
  onDragCancel: () => "جابه‌جایی لغو شد.",
};

function SortableMenuItem({ id, index, total, disabled, move }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const label = labels.get(id);
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`relative flex items-center gap-3 rounded-xl border bg-white p-3 dark:bg-slate-900 sm:p-4 ${isDragging ? "z-10 border-blue-400 shadow-lg ring-2 ring-blue-100 dark:ring-blue-900" : "border-slate-200 dark:border-slate-700"}`}
    >
      <button
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        type="button"
        disabled={disabled}
        aria-label={`جابه‌جایی ${label}`}
        title="برای جابه‌جایی بکشید؛ با صفحه‌کلید از فاصله و کلیدهای جهت استفاده کنید"
        style={{ touchAction: "none" }}
        className="flex h-11 w-11 shrink-0 cursor-grab items-center justify-center rounded-lg bg-slate-100 text-slate-500 active:cursor-grabbing disabled:cursor-wait focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 dark:bg-slate-800 dark:text-slate-300"
      >
        <FaGripVertical aria-hidden="true" />
      </button>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-sm font-semibold text-blue-700 dark:bg-blue-950 dark:text-blue-300">{number.format(index + 1)}</span>
      <span className="min-w-0 flex-1 text-sm font-semibold text-slate-700 dark:text-slate-100 sm:text-base">{label}</span>
      <div className="flex shrink-0 gap-1">
        <button type="button" disabled={disabled || index === 0} onClick={() => move(index, index - 1)} aria-label={`انتقال ${label} به بالا`} className="flex h-10 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 dark:text-slate-300 dark:hover:bg-slate-800"><FaArrowUp aria-hidden="true" /></button>
        <button type="button" disabled={disabled || index === total - 1} onClick={() => move(index, index + 1)} aria-label={`انتقال ${label} به پایین`} className="flex h-10 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 dark:text-slate-300 dark:hover:bg-slate-800"><FaArrowDown aria-hidden="true" /></button>
      </div>
    </li>
  );
}

export default function SidebarSettings() {
  const { order, isLoading, isSaving, loadError, reload, saveOrder } = useSidebarSettings();
  const [editedOrder, setDraft] = useState(null);
  const draft = editedOrder ?? order;
  const [saveError, setSaveError] = useState("");
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const dirty = draft.join(",") !== order.join(",");
  const disabled = isSaving || isLoading || Boolean(loadError);
  const move = (from, to) => {
    if (!disabled) setDraft((items) => arrayMove(items ?? order, from, to));
  };
  const handleDragEnd = ({ active, over }) => {
    if (over && active.id !== over.id) move(draft.indexOf(active.id), draft.indexOf(over.id));
  };
  const save = async () => {
    if (disabled || !dirty) return;
    setSaveError("");
    try {
      await saveOrder(draft);
      setDraft(null);
      successMessage("ترتیب سایدبار ذخیره و اعمال شد.");
    } catch (error) {
      const message = error?.response?.data?.errors?.[0]?.message;
      setSaveError(typeof message === "string" ? message : "ذخیره تغییرات تأیید نشد؛ دوباره تلاش کنید.");
    }
  };

  return (
    <section dir="rtl" className="rounded-3xl bg-white p-4 dark:bg-slate-900 sm:p-6">
      <div className="mb-6 border-b border-slate-100 pb-5 dark:border-slate-800">
        <p className="mb-2 text-xs font-medium text-slate-400">تنظیمات</p>
        <Title title="تنظیمات سایدبار" icon={<PiSlidersBold size={23} className="text-blue-600 dark:text-blue-400" />} />
        <p className="mt-3 text-sm leading-7 text-slate-500 dark:text-slate-400">
          آیتم‌های اصلی را از دستگیره کنار نامشان بکشید و در جای دلخواه رها کنید. زیرمنوها همراه آیتم اصلی جابه‌جا می‌شوند و ترتیبشان ثابت می‌ماند. تغییرات پس از ذخیره برای همه مدیران اعمال می‌شود.
        </p>
      </div>

      {loadError && <div role="alert" className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-amber-50 p-4 text-sm text-amber-800 dark:bg-amber-950 dark:text-amber-200"><span>{loadError}</span><button type="button" onClick={reload} className="rounded-lg border border-amber-300 px-4 py-2 font-semibold">تلاش دوباره</button></div>}
      {saveError && <p role="alert" className="mb-5 rounded-xl bg-red-50 p-4 text-sm leading-7 text-red-700 dark:bg-red-950 dark:text-red-200">{saveError}</p>}

      {isLoading ? <p role="status" className="py-10 text-center text-sm text-slate-500">در حال دریافت تنظیمات…</p> : (
        <div className="max-w-3xl">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-slate-600 dark:text-slate-300">ترتیب نمایش آیتم‌های اصلی</h2>
            <button type="button" disabled={disabled} onClick={() => { setDraft([...DEFAULT_SIDEBAR_ORDER]); setSaveError(""); }} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"><FaUndo aria-hidden="true" />ترتیب پیش‌فرض</button>
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[verticalOnly]}
            onDragEnd={handleDragEnd}
            accessibility={{
              announcements,
              screenReaderInstructions: {
                draggable: "برای شروع جابه‌جایی کلید فاصله را بزنید، با جهت بالا و پایین حرکت کنید و برای ثبت جای جدید دوباره فاصله را بزنید. برای لغو Escape را بزنید.",
              },
            }}
          >
            <SortableContext items={draft} strategy={verticalListSortingStrategy}>
              <ol aria-label="ترتیب آیتم‌های اصلی سایدبار" className="space-y-2">
                {draft.map((id, index) => <SortableMenuItem key={id} id={id} index={index} total={draft.length} disabled={disabled} move={move} />)}
              </ol>
            </SortableContext>
          </DndContext>
          <div className="sticky bottom-4 z-20 mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-lg dark:border-slate-700 dark:bg-slate-900">
            <p role="status" className="text-xs leading-6 text-slate-500 dark:text-slate-400">{isSaving ? "در حال ذخیره تغییرات…" : dirty ? "تغییرات هنوز ذخیره نشده‌اند." : "ترتیب نمایش با تنظیمات ذخیره‌شده یکسان است."}</p>
            <div className="flex flex-wrap gap-2">
              <button type="button" disabled={disabled || !dirty} onClick={() => { setDraft(null); setSaveError(""); }} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-600 disabled:opacity-40 dark:border-slate-700 dark:text-slate-300">لغو تغییرات</button>
              <button type="button" disabled={disabled || !dirty} onClick={save} className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"><FaSave aria-hidden="true" />{isSaving ? "در حال ذخیره…" : "ذخیره"}</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
